import React from "react";
import GemIcon from "./GemIcon";
import { monthLabel } from "./ui";
import "./Dashboard.css";

const usd = (n) => "$" + Math.round(n || 0).toLocaleString();
const ROLES = ["CNA", "LPN", "RN"];
const DIVISIONS = ["Facility", "GEM"];

function divisionTotals(bd, div) {
  let open = 0, co = 0;
  for (const role of ROLES) { open += bd[div]?.[role]?.open || 0; co += bd[div]?.[role]?.co || 0; }
  return { open, co, total: co }; // headline total = Completed/Ongoing; Open shown separately
}

export default function Dashboard({ data }) {
  const { totals, byDivision, hoursByDivRole, clients, weeks, budget, context } = data;
  const maxWeek = Math.max(1, ...weeks.map((w) => w.revenue));
  const budgetPct = budget.total ? Math.min(100, Math.round((budget.forecast / budget.total) * 100)) : null;

  return (
    <div className="dash">
      {/* Last-updated banner */}
      <div className="updated-bar">
        <span className="updated-dot" />
        Last updated <strong>{data.lastUpdated ? new Date(data.lastUpdated).toLocaleString() : "—"}</strong>
        <span className="updated-src">· {data.source || "CSV upload"}{data.filename ? ` · ${data.filename}` : ""}</span>
      </div>

      {/* KPI cards */}
      <section className="kpis">
        <div className="kpi kpi-primary">
          <div className="kpi-label">Forecast — {data.monthLabel}</div>
          <div className="kpi-value">{usd(totals.completedOngoing)}</div>
          <div className="kpi-sub">Completed / Ongoing · +{usd(totals.openRevenue)} open (unfilled)</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Budget</div>
          <div className="kpi-value">{budget.total ? usd(budget.total) : "—"}</div>
          {budget.variance != null && (
            <div className={"kpi-sub " + (budget.variance >= 0 ? "pos" : "neg")}>
              {budget.variance >= 0 ? "+" : ""}{usd(budget.variance)} vs budget
            </div>
          )}
        </div>
        <div className="kpi">
          <div className="kpi-label">Prior month {context.prevMonthEstimated ? "(estimated)" : "final"}</div>
          <div className="kpi-value">{context.prevMonthFinal != null ? (context.prevMonthEstimated ? "~" : "") + usd(context.prevMonthFinal) : "—"}</div>
          <div className="kpi-sub">{monthLabel(context.prevMonth)}{context.prevMonthEstimated ? " · est. until P&L finalized" : ""}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Billable hours</div>
          <div className="kpi-value">{Math.round(totals.hours.CNA + totals.hours.LPN + totals.hours.RN).toLocaleString()}</div>
          <div className="kpi-sub">CNA {Math.round(totals.hours.CNA)} · LPN {Math.round(totals.hours.LPN)} · RN {Math.round(totals.hours.RN)}</div>
        </div>
      </section>

      {/* Budget vs forecast bar */}
      {budgetPct != null && (
        <section className="panel">
          <div className="panel-head"><h2>Budget vs Forecast</h2><span>{budgetPct}% of budget</span></div>
          <div className="progress"><div className="progress-fill" style={{ width: budgetPct + "%" }} /></div>
          <div className="progress-labels"><span>{usd(budget.forecast)} forecast</span><span>{usd(budget.total)} budget</span></div>
        </section>
      )}

      {/* Division × role × bucket */}
      <section className="panel">
        <div className="panel-head"><h2>Forecast by Division &amp; Role</h2></div>
        <table className="grid">
          <thead>
            <tr>
              <th>Division</th>
              {ROLES.map((r) => <th key={r} colSpan={2}>{r}</th>)}
              <th>Total</th>
            </tr>
            <tr className="subhead">
              <th></th>
              {ROLES.map((r) => <React.Fragment key={r}><th>C/O</th><th>Open</th></React.Fragment>)}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {DIVISIONS.map((div) => {
              const t = divisionTotals(byDivision, div);
              return (
                <tr key={div}>
                  <td className="rowhead">{div}</td>
                  {ROLES.map((r) => (
                    <React.Fragment key={r}>
                      <td>{usd(byDivision[div]?.[r]?.co)}</td>
                      <td className="muted">{usd(byDivision[div]?.[r]?.open)}</td>
                    </React.Fragment>
                  ))}
                  <td className="strong">{usd(t.total)}</td>
                </tr>
              );
            })}
            <tr className="total-row">
              <td className="rowhead">All</td>
              {ROLES.map((r) => {
                const co = DIVISIONS.reduce((s, d) => s + (byDivision[d]?.[r]?.co || 0), 0);
                const open = DIVISIONS.reduce((s, d) => s + (byDivision[d]?.[r]?.open || 0), 0);
                return <React.Fragment key={r}><td>{usd(co)}</td><td className="muted">{usd(open)}</td></React.Fragment>;
              })}
              <td className="strong">{usd(totals.completedOngoing)}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Hours by division & role */}
      <section className="panel">
        <div className="panel-head"><h2>Billable Hours by Division &amp; Role</h2></div>
        <table className="grid">
          <thead><tr><th>Division</th>{ROLES.map((r) => <th key={r}>{r}</th>)}<th>Total</th></tr></thead>
          <tbody>
            {DIVISIONS.map((div) => {
              const h = hoursByDivRole[div] || { CNA: 0, LPN: 0, RN: 0 };
              const tot = h.CNA + h.LPN + h.RN;
              return <tr key={div}><td className="rowhead">{div}</td>{ROLES.map((r) => <td key={r}>{Math.round(h[r]).toLocaleString()}</td>)}<td className="strong">{Math.round(tot).toLocaleString()}</td></tr>;
            })}
          </tbody>
        </table>
      </section>

      {/* Weekly */}
      <section className="panel">
        <div className="panel-head"><h2>Weekly Revenue</h2></div>
        <div className="weeks">
          {weeks.map((w) => (
            <div key={w.ws} className="week">
              <div className="week-bar-wrap"><div className="week-bar" style={{ height: Math.max(4, (w.revenue / maxWeek) * 120) + "px" }} /></div>
              <div className="week-val">{usd(w.revenue)}</div>
              <div className="week-label">{w.ws.slice(5)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Clients */}
      <section className="panel">
        <div className="panel-head"><h2>By Client</h2><span>{clients.length} active</span></div>
        <table className="grid clients">
          <thead><tr><th>Client</th><th>Division</th><th>CNA</th><th>LPN</th><th>RN</th><th>Total</th></tr></thead>
          <tbody>
            {clients.map((c) => {
              const cell = (r) => usd((c.roles[r]?.co || 0) + (c.roles[r]?.open || 0));
              return (
                <tr key={c.name}>
                  <td className="rowhead">{c.name}</td>
                  <td>
                    <span className="gem-cell">
                      <span className={"pill pill-" + c.division.toLowerCase()}>{c.division}</span>
                      {c.gemStage && (
                        <span className="gem-badge" title={`GEM Stage: ${c.gemStage} — the client's stage in the dementia progression (Teepa Snow GEMS model).`}>
                          <GemIcon type={c.gemStage} />
                          <span className="gem-name">{c.gemStage}</span>
                        </span>
                      )}
                    </span>
                  </td>
                  <td>{cell("CNA")}</td><td>{cell("LPN")}</td><td>{cell("RN")}</td>
                  <td className="strong">{usd(c.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
